// src/pages/AdminDashboard.jsx
// 管理者儀表板 - 顯示所有商家和統計數據（真實資料 + 設定功能）

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store,
  Package,
  TrendingUp,
  ShoppingCart,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  ArrowRight,
  AlertTriangle,
  Settings,
  LogOut,
  User
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [adminName, setAdminName] = useState('');

  useEffect(() => {
    // 獲取管理員資訊
    const name = localStorage.getItem('adminName');
    if (name) setAdminName(name);
    
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // 載入儀表板數據
      const dashResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dashResult = await dashResponse.json();
      
      if (dashResult.error) throw new Error(dashResult.message);
      setDashboard(dashResult.data);

      // 載入所有商家
      const sellersResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/sellers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sellersResult = await sellersResponse.json();
      
      if (sellersResult.error) throw new Error(sellersResult.message);
      setSellers(sellersResult.data.sellers || []);

    } catch (err) {
      setError(err.message);
      console.error('載入儀表板失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sellerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/sellers/${sellerId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      loadDashboard();
      setDeleteConfirm(null);
      alert('商家刪除成功！');
    } catch (err) {
      alert('刪除失敗: ' + err.message);
    }
  };

  const handleLogout = () => {
    if (window.confirm('確定要登出嗎？')) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('adminRole');
      localStorage.removeItem('adminName');
      localStorage.removeItem('adminUsername');
      localStorage.removeItem('adminEmail');
      navigate('/admin/login');
    }
  };

  const filteredSellers = sellers.filter(seller =>
    seller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    seller.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-4">載入失敗</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadDashboard}
            className="w-full bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600"
          >
            重試
          </button>
        </div>
      </div>
    );
  }

  const { total_sellers, product_stats, recent_sellers } = dashboard || {};

  // 統計卡片數據
  const statCards = [
    {
      icon: Store,
      label: '總商家數',
      value: total_sellers || 0,
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      icon: Package,
      label: '總商品數',
      value: product_stats?.total_products || 0,
      color: 'purple',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      icon: ShoppingCart,
      label: '總銷量',
      value: product_stats?.total_sold || 0,
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      icon: TrendingUp,
      label: '總庫存',
      value: product_stats?.total_stock || 0,
      color: 'orange',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">管理者儀表板</h1>
              <p className="mt-1 text-gray-600">
                商家與商品管理
                {adminName && <span className="ml-2">· 歡迎，{adminName}</span>}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* 新增商家按鈕 */}
              <button
                onClick={() => navigate('/admin/sellers/new')}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">新增商家</span>
              </button>
              
              {/* 設定按鈕 */}
              <button
                onClick={() => navigate('/admin/settings')}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="帳號設定"
              >
                <Settings size={20} />
                <span className="hidden sm:inline">設定</span>
              </button>
              
              {/* 登出按鈕 */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="登出"
              >
                <LogOut size={20} />
                <span className="hidden sm:inline">登出</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                </div>
                <div className={`${card.bgColor} p-3 rounded-lg`}>
                  <card.icon size={24} className={card.iconColor} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 商家列表 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">所有商家</h2>
              <div className="relative w-64">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜尋商家..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {filteredSellers.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Store size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">沒有商家</p>
              <button
                onClick={() => navigate('/admin/sellers/new')}
                className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                新增第一個商家
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">商家名稱</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">地點</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">評分</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">回應率</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">商品數</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">總銷量</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-700">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSellers.map(seller => (
                    <tr key={seller.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="font-medium text-gray-900">{seller.name}</div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-gray-600">{seller.location}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-medium text-yellow-600">
                          ⭐ {Number(seller.rating).toFixed(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-gray-600">{seller.response_rate}%</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-medium">{seller.product_count || 0}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-medium">{seller.total_sold || 0}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/sellers/${seller.id}/products`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="查看商品"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/sellers/${seller.id}/edit`)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                            title="編輯"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(seller)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="刪除"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 刪除確認對話框 */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-red-50 rounded-full">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  確認刪除商家
                </h3>
                <p className="text-gray-600 mb-2">
                  您確定要刪除商家「{deleteConfirm.name}」嗎？
                </p>
                {deleteConfirm.product_count > 0 && (
                  <p className="text-red-600 text-sm">
                    ⚠️ 此商家有 {deleteConfirm.product_count} 個商品，請先刪除所有商品。
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleteConfirm.product_count > 0}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;